import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trash2, Plus, ArrowLeft, Truck, Edit2, Phone, Mail, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';

const VendorManagement = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [stats, setStats] = useState({
    total_vendors: 0,
    active_vendors: 0,
    inactive_vendors: 0
  });
  
  const [vendorForm, setVendorForm] = useState({
    name: '',
    email: '',
    phone: '',
    whatsapp_number: '',
    address: '',
    service_areas: '',
    is_active: true
  });

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors(response.data);
      
      const activeVendors = response.data.filter(v => v.is_active).length;
      setStats({
        total_vendors: response.data.length,
        active_vendors: activeVendors,
        inactive_vendors: response.data.length - activeVendors
      });
    } catch (error) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVendor = async (e) => {
    e.preventDefault();
    
    if (!vendorForm.name || !vendorForm.email || !vendorForm.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingVendor) {
        // Update existing vendor
        await axios.put(
          `${API}/admin/vendors/${editingVendor.id}`,
          vendorForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Vendor updated successfully');
      } else {
        // Create new vendor
        await axios.post(
          `${API}/admin/vendors`,
          vendorForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Vendor created successfully');
      }
      setShowDialog(false);
      setEditingVendor(null);
      resetForm();
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save vendor');
    }
  };

  const handleEditVendor = (vendor) => {
    setEditingVendor(vendor);
    setVendorForm({
      name: vendor.name,
      email: vendor.email,
      phone: vendor.phone || '',
      whatsapp_number: vendor.whatsapp_number || '',
      address: vendor.address || '',
      service_areas: vendor.service_areas || '',
      is_active: vendor.is_active
    });
    setShowDialog(true);
  };

  const handleToggleActive = async (vendor) => {
    try {
      await axios.put(
        `${API}/admin/vendors/${vendor.id}`,
        { ...vendor, is_active: !vendor.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Vendor ${!vendor.is_active ? 'activated' : 'deactivated'}`);
      fetchVendors();
    } catch (error) {
      toast.error('Failed to update vendor status');
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;
    
    try {
      await axios.delete(`${API}/admin/vendors/${vendorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vendor deleted successfully');
      fetchVendors();
    } catch (error) {
      toast.error('Failed to delete vendor');
    }
  };

  const resetForm = () => {
    setVendorForm({
      name: '',
      email: '',
      phone: '',
      whatsapp_number: '',
      address: '',
      service_areas: '',
      is_active: true
    });
  };

  return (
    <div className="min-h-screen" style={{background: 'var(--background)'}}>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/super-admin')}
                className="flex items-center space-x-2"
                data-testid="back-button"
              >
                <ArrowLeft className="h-5 w-5" />
                <span>Back to Dashboard</span>
              </Button>
              <h1 className="text-2xl font-bold" style={{color: 'var(--foreground)'}}>Vendor Management</h1>
            </div>
            
            <Dialog open={showDialog} onOpenChange={(open) => {
              setShowDialog(open);
              if (!open) {
                setEditingVendor(null);
                resetForm();
              }
            }}>
              <DialogTrigger asChild>
                <Button 
                  className="flex items-center space-x-2"
                  style={{backgroundColor: 'var(--primary)'}}
                  data-testid="add-vendor-button"
                >
                  <Plus className="h-5 w-5" />
                  <span>Add Vendor</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>{editingVendor ? 'Edit Vendor' : 'Add New Vendor'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSaveVendor} className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="name">Vendor Name *</Label>
                    <Input
                      id="name"
                      value={vendorForm.name}
                      onChange={(e) => setVendorForm({...vendorForm, name: e.target.value})}
                      placeholder="Enter vendor/company name"
                      data-testid="vendor-name-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={vendorForm.email}
                        onChange={(e) => setVendorForm({...vendorForm, email: e.target.value})}
                        placeholder="vendor@email.com"
                        data-testid="vendor-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone *</Label>
                      <Input
                        id="phone"
                        value={vendorForm.phone}
                        onChange={(e) => setVendorForm({...vendorForm, phone: e.target.value})}
                        placeholder="+91 XXXXX XXXXX"
                        data-testid="vendor-phone-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="whatsapp">WhatsApp Number</Label>
                    <Input
                      id="whatsapp"
                      value={vendorForm.whatsapp_number}
                      onChange={(e) => setVendorForm({...vendorForm, whatsapp_number: e.target.value})}
                      placeholder="+91 XXXXX XXXXX"
                      data-testid="vendor-whatsapp-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      value={vendorForm.address}
                      onChange={(e) => setVendorForm({...vendorForm, address: e.target.value})}
                      placeholder="Full address"
                      data-testid="vendor-address-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="service_areas">Service Areas (Pincodes)</Label>
                    <Input
                      id="service_areas"
                      value={vendorForm.service_areas}
                      onChange={(e) => setVendorForm({...vendorForm, service_areas: e.target.value})}
                      placeholder="560001, 560002, 560003..."
                      data-testid="vendor-areas-input"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_active"
                      checked={vendorForm.is_active}
                      onCheckedChange={(checked) => setVendorForm({...vendorForm, is_active: checked})}
                    />
                    <Label htmlFor="is_active">Active</Label>
                  </div>
                  
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" style={{backgroundColor: 'var(--primary)'}} data-testid="save-vendor-button">
                      {editingVendor ? 'Update Vendor' : 'Add Vendor'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Vendors</p>
                <p className="text-3xl font-bold text-gray-900" data-testid="total-vendors">{stats.total_vendors}</p>
              </div>
              <Truck className="h-10 w-10" style={{color: 'var(--primary)'}} />
            </div>
          </div>
          
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Vendors</p>
                <p className="text-3xl font-bold text-green-600" data-testid="active-vendors">{stats.active_vendors}</p>
              </div>
              <CheckCircle className="h-10 w-10 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Inactive Vendors</p>
                <p className="text-3xl font-bold text-gray-500" data-testid="inactive-vendors">{stats.inactive_vendors}</p>
              </div>
              <XCircle className="h-10 w-10 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Vendors Table */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Vendor Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Contact</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">WhatsApp</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Service Areas</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">Loading vendors...</td>
                  </tr>
                ) : vendors.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      <Truck className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                      <p>No vendors found. Add your first vendor!</p>
                    </td>
                  </tr>
                ) : (
                  vendors.map((vendor) => (
                    <tr key={vendor.id} data-testid={`vendor-row-${vendor.id}`}>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-semibold text-gray-900">{vendor.name}</p>
                          {vendor.address && (
                            <p className="text-xs text-gray-500 truncate max-w-[200px]">{vendor.address}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="flex items-center text-sm text-gray-600">
                            <Mail className="h-3 w-3 mr-1" />
                            {vendor.email}
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <Phone className="h-3 w-3 mr-1" />
                            {vendor.phone}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {vendor.whatsapp_number || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <span className="truncate max-w-[150px] block">{vendor.service_areas || 'All areas'}</span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleToggleActive(vendor)}
                          className={`text-xs font-semibold px-3 py-1 rounded-full ${
                            vendor.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {vendor.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditVendor(vendor)}
                            style={{color: 'var(--accent)'}}
                            data-testid={`edit-vendor-${vendor.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteVendor(vendor.id)}
                            className="text-red-500 hover:text-red-600"
                            data-testid={`delete-vendor-${vendor.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VendorManagement;
